import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const titleAnim = useScrollAnimation();
  const leftAnim = useScrollAnimation({ rootMargin: '0px 0px -60px 0px' });
  const formAnim = useScrollAnimation({ rootMargin: '0px 0px -60px 0px' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.category || !formData.message) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('company', formData.company);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('category', formData.category);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6bdcbnmvg9ih2c7b4c0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', category: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div
          ref={titleAnim.ref}
          className={titleAnim.isVisible ? 'anim-fade-up' : 'anim-hidden'}
        >
          <div className="mb-8 md:mb-12">
            <div className="flex items-center gap-3 mb-3 md:mb-4">
              <img
                src="https://static.readdy.ai/image/cb8749fdfddc72e72c266efb2e07a84d/758ef2cf37f252b690d6ec189d9cae34.png"
                alt=""
                className="h-6 md:h-8"
              />
              <h2 className="text-3xl md:text-5xl font-black text-gray-900">
                お問い合わせ
              </h2>
            </div>
            <p className="text-xs md:text-sm text-gray-500 tracking-widest">
              Contact
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Left info panel */}
          <div
            ref={leftAnim.ref}
            className={`lg:col-span-2 ${leftAnim.isVisible ? 'anim-fade-left anim-delay-200' : 'anim-hidden'}`}
          >
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm mb-6">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                お気軽にご相談ください
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                運送に関するご質問やお見積もりのご依頼など、どんなことでもお気軽にお問い合わせください。担当者より折り返しご連絡いたします。
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">所在地</p>
                    <p className="text-sm text-gray-900 font-medium">
                      〒497-0006<br />愛知県あま市七宝町下之森一丁田22番、23番
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">メール</p>
                    <p className="text-sm text-gray-900 font-medium">
                      info@houjyu-logistics.jp
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">営業時間</p>
                    <p className="text-sm text-gray-900 font-medium">
                      月〜金 8:00 - 17:00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden shadow-sm">
              <img
                src="https://static.readdy.ai/image/cb8749fdfddc72e72c266efb2e07a84d/534b089c0ce9f6450587315e57d2a402.png"
                alt="宝珠運輸のトラック"
                className="w-full h-full object-contain object-center"
              />
            </div>
          </div>

          {/* Right form panel */}
          <div
            ref={formAnim.ref}
            className={`lg:col-span-3 ${formAnim.isVisible ? 'anim-fade-right anim-delay-300' : 'anim-hidden'}`}
          >
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl p-6 md:p-8 shadow-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="山田 太郎"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="株式会社〇〇"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@mail.com"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="052-000-0000"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                  />
                </div>
              </div>

              <div className="mb-4 md:mb-5">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1.5">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors bg-white appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                  }}
                >
                  <option value="">選択してください</option>
                  <option value="運送サービスについて">運送サービスについて</option>
                  <option value="お見積もり依頼">お見積もり依頼</option>
                  <option value="採用について">採用について</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="お問い合わせ内容をご記入ください"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.message.length} / 500
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-teal-500 text-white text-sm font-bold rounded-lg hover:bg-teal-600 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    送信中...
                  </span>
                ) : (
                  '送信する'
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-500 text-white shrink-0">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <p className="text-sm text-teal-800">
                    お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-500 text-white shrink-0">
                    <i className="ri-close-line text-lg"></i>
                  </div>
                  <p className="text-sm text-red-800">
                    送信に失敗しました。時間をおいて再度お試しください。
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
